import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  HeartHandshake, Award, Image, AlertCircle, Milestone, Newspaper,
  Inbox, Users, ArrowRight, Loader2, CheckCircle2, MapPin, Tag,
} from "lucide-react";
import { getDashboard } from "@/lib/api";

const STAT_CARDS = [
  { key: "socialWork", label: "Social Work Posts", icon: HeartHandshake, color: "bg-civic", to: "/admin/resource/social-work" },
  { key: "awards", label: "Awards", icon: Award, color: "bg-gold", to: "/admin/resource/awards" },
  { key: "gallery", label: "Gallery Images", icon: Image, color: "bg-navy", to: "/admin/resource/gallery" },
  { key: "wardIssues", label: "Hoodi Ward Issues", icon: AlertCircle, color: "bg-red-500", to: "/admin/ward-issues" },
  { key: "newIssues", label: "New Hoodi Issues", icon: AlertCircle, color: "bg-amber-500", to: "/admin/ward-issues" },
  { key: "resolvedIssues", label: "Resolved Hoodi Issues", icon: CheckCircle2, color: "bg-emerald-500", to: "/admin/ward-issues" },
  { key: "milestones", label: "Milestones", icon: Milestone, color: "bg-purple-500", to: "/admin/resource/milestones" },
  { key: "media", label: "Media Items", icon: Newspaper, color: "bg-blue-500", to: "/admin/resource/media" },
  { key: "contacts", label: "Contact Inquiries", icon: Inbox, color: "bg-emerald-600", to: "/admin/submissions" },
  { key: "joins", label: "Join Requests", icon: Users, color: "bg-orange-500", to: "/admin/submissions" },
];

export default function AdminDashboard() {
  const { data, isLoading } = useQuery({ queryKey: ["dashboard"], queryFn: getDashboard });

  if (isLoading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;

  const counts = data?.counts || {};

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="font-display text-2xl md:text-3xl font-semibold text-navy">Dashboard</h1>
        <p className="text-slate-500 text-sm mt-1">Overview of content and citizen engagement for Hoodi Ward 29.</p>
        {counts.newIssues > 0 && (
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-50 text-red-600 text-sm font-medium">
            <AlertCircle className="h-4 w-4" /> {counts.newIssues} new ward issue(s) awaiting review
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {STAT_CARDS.map((s) => (
          <Link key={s.label} to={s.to} className="bg-white rounded-2xl p-5 border border-slate-200 hover:shadow-lg hover:-translate-y-0.5 transition-all">
            <div className={`h-11 w-11 rounded-xl ${s.color} flex items-center justify-center mb-4`}>
              <s.icon className="h-5 w-5 text-white" />
            </div>
            <p className="font-display text-3xl font-bold text-navy">{counts[s.key] ?? 0}</p>
            <p className="text-xs text-slate-500 mt-1">{s.label}</p>
          </Link>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
            <Tag className="h-4 w-4 text-gold" /><h2 className="font-semibold text-navy">Hoodi Issues by Category</h2>
          </div>
          <div className="p-5 space-y-2">
            {(data?.byCategory || []).length === 0 && <p className="text-sm text-slate-400">No issues yet.</p>}
            {(data?.byCategory || []).map((c) => (
              <div key={c._id || "na"} className="flex items-center justify-between text-sm">
                <span className="text-slate-600">{c._id || "—"}</span>
                <span className="font-semibold text-navy px-2 py-0.5 rounded-full bg-slate-100">{c.count}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
            <MapPin className="h-4 w-4 text-civic" /><h2 className="font-semibold text-navy">Hoodi Issues by Locality</h2>
          </div>
          <div className="p-5 space-y-2">
            {(data?.byLocality || []).length === 0 && <p className="text-sm text-slate-400">No issues yet.</p>}
            {(data?.byLocality || []).map((c) => (
              <div key={c._id || "na"} className="flex items-center justify-between text-sm">
                <span className="text-slate-600 truncate pr-3">{c._id || "—"}</span>
                <span className="font-semibold text-navy px-2 py-0.5 rounded-full bg-slate-100">{c.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <h2 className="font-semibold text-navy">Latest Hoodi Ward Issues</h2>
            <Link to="/admin/ward-issues" className="text-sm text-gold font-medium flex items-center gap-1">View all <ArrowRight className="h-3.5 w-3.5" /></Link>
          </div>
          <div className="divide-y divide-slate-100">
            {(data?.latestIssues || []).length === 0 && <p className="px-5 py-8 text-center text-slate-400 text-sm">No issues yet.</p>}
            {(data?.latestIssues || []).map((it) => (
              <div key={it.id} className="px-5 py-3 flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-navy truncate">{it.title}</p>
                  <p className="text-xs text-slate-400">{it.category} · {it.locality}</p>
                </div>
                <span className="text-[11px] px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 shrink-0">{it.status}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <h2 className="font-semibold text-navy">Latest Social Work Posts</h2>
            <Link to="/admin/resource/social-work" className="text-sm text-gold font-medium flex items-center gap-1">Manage <ArrowRight className="h-3.5 w-3.5" /></Link>
          </div>
          <div className="divide-y divide-slate-100">
            {(data?.latestPosts || []).length === 0 && <p className="px-5 py-8 text-center text-slate-400 text-sm">No posts yet.</p>}
            {(data?.latestPosts || []).map((p) => (
              <div key={p.id} className="px-5 py-3 flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-navy truncate">{p.title}</p>
                  <p className="text-xs text-slate-400">{p.category} · {p.date}</p>
                </div>
                <span className="text-[11px] px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 shrink-0">{p.status}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
